import axios, { AxiosError, AxiosRequestConfig } from 'axios';

export interface SessionInfo {
    expiresAt: string;
    expiresInSeconds: number;
}

export const SESSION_EXPIRY_KEY = '@chamados:sessionExpiresAt';
export const SESSION_EXPIRED_EVENT = 'chamados:session-expired';
export const SESSION_RENEWED_EVENT = 'chamados:session-renewed';

const getBaseUrl = () => {
    const configuredUrl = process.env.REACT_APP_API_URL?.trim();
    if (configuredUrl) return configuredUrl.replace(/\/$/, '');
    if (process.env.NODE_ENV === 'production') return '/api';
    return `${window.location.protocol}//${window.location.hostname}:8181/api`;
};

const api = axios.create({
    baseURL: getBaseUrl(),
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

const readActiveSecretaryId = (): string | null => {
    try {
        const raw = localStorage.getItem('@chamados:activeSecretary');
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.id === 'string' ? parsed.id : null;
    } catch {
        localStorage.removeItem('@chamados:activeSecretary');
        return null;
    }
};

api.interceptors.request.use((config) => {
    const secretaryId = readActiveSecretaryId();
    if (secretaryId) config.headers.set('X-Active-Secretary-Id', secretaryId);
    try {
        const context = JSON.parse(localStorage.getItem('@chamados:activeSecretary') || 'null');
        if (context?.itTeam?.id) config.headers.set('X-Active-Team-Id', context.itTeam.id);
    } catch { /* O contexto inválido será descartado na autenticação. */ }
    return config;
});

let refreshPromise: Promise<SessionInfo> | null = null;

const renewSession = async () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            // Garante que o primeiro clique e a recuperação automática usem o
            // token CSRF mais recente emitido pelo Spring Security.
            await api.get('/auth/csrf');
            try {
                const response = await api.post<SessionInfo>('/auth/refresh', {});
                return response.data;
            } catch (error) {
                if (!axios.isAxiosError(error) || error.response?.status !== 403) throw error;
                await api.get('/auth/csrf');
                const response = await api.post<SessionInfo>('/auth/refresh', {});
                return response.data;
            }
        })()
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
};

api.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
        const requestUrl = error.config?.url ?? '';
        const isPublicAuthEndpoint = requestUrl.includes('/auth/login')
            || requestUrl.includes('/auth/refresh')
            || requestUrl.includes('/auth/logout')
            || requestUrl.includes('/auth/csrf')
            || requestUrl.includes('/auth/me')
            || requestUrl.includes('/auth/session')
            || requestUrl.includes('/auth/forgot')
            || requestUrl.includes('/auth/reset');

        if (error.response?.status === 401 && !isPublicAuthEndpoint) {
            const requestConfig = error.config as (typeof error.config & {
                _sessionRecoveryAttempted?: boolean;
            });
            const expectedExpiration = Number(localStorage.getItem(SESSION_EXPIRY_KEY));
            const sessionShouldStillBeValid = Number.isFinite(expectedExpiration)
                && expectedExpiration > Date.now();

            // Se o navegador ainda espera uma sessão válida, renova o token e
            // repete uma única vez a operação original. Expirações reais não
            // são prorrogadas silenciosamente.
            if (requestConfig && !requestConfig._sessionRecoveryAttempted && sessionShouldStillBeValid) {
                requestConfig._sessionRecoveryAttempted = true;
                try {
                    const session = await renewSession();
                    const expiresAt = Date.parse(session.expiresAt);
                    if (Number.isFinite(expiresAt)) {
                        localStorage.setItem(SESSION_EXPIRY_KEY, String(expiresAt));
                        window.dispatchEvent(new CustomEvent<SessionInfo>(SESSION_RENEWED_EVENT, {
                            detail: session,
                        }));
                    }
                    return api.request(requestConfig);
                } catch {
                    // A sessão será encerrada abaixo se o refresh também falhar.
                }
            }

            window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
                detail: { expiresAt: expectedExpiration, force: true },
            }));
        }

        return Promise.reject(error);
    }
);

export const initializeCsrfProtection = async (): Promise<void> => {
    await api.get('/auth/csrf');
};

export const refreshSession = renewSession;

export const getSessionInfo = async (): Promise<SessionInfo> => {
    const response = await api.get<SessionInfo>('/auth/session');
    return response.data;
};

export const request = async <T = unknown>(config: AxiosRequestConfig): Promise<T> => {
    const response = await api.request<T>(config);
    return response.data;
};

export default api;
